import { useState, useEffect, useMemo } from "react";
import { Search, Plus, Users, Edit2, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Employee } from "../../types/employee";
import EmployeeSidebar from "../../components/EmployeeSidebar";
import EmployeeDetailsSidebar from "../../components/EmployeeDetailsSidebar";
import api from "../../services/api";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { cn } from "../../lib/utils";
import { useToast } from "../../hooks/useToast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { DeleteConfirmModal } from "../../components/categories/DeleteConfirmModal";

export default function EmployeePage() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [isDetailsSidebarOpen, setIsDetailsSidebarOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch employees
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setFetchLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/employees`);
      const data = response.data;

      let employeeList: Employee[] = [];

      if (data.employees) {
        employeeList = data.employees;
      } else if (data.success && data.data) {
        if (data.data.data) {
          employeeList = data.data.data;
        } else {
          employeeList = data.data;
        }
      }

      setEmployees(employeeList);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch employees");
      console.error("Error fetching employees:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditing(null);
    setIsSidebarOpen(true);
    setError(null);
  };

  const handleEdit = (emp: Employee) => {
    setEditing(emp);
    setIsSidebarOpen(true);
    setError(null);
  };

  const handleViewDetails = (emp: Employee) => {
    setSelectedEmployee(emp);
    setIsDetailsSidebarOpen(true);
  };

  const handleSubmit = async (data: Partial<Employee> & {
    imageFile?: File | null;
    role?: string;
    role_id?: number | null;
    shift_id?: number | null;
    emp_joining_date?: string;
    work_type?: string;
    hourly_rate?: string;
    fixed_price?: string;
    working_days?: string;
    commission_per_booking?: string;
    employee_group?: string;
  }) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("name", data.name || "");
      formData.append("email", data.email || "");

      if (data.role) formData.append("role", data.role);
      if (data.status) formData.append("status", data.status);
      if (data.mobile) formData.append("mobile", data.mobile);
      if (data.calling_code) formData.append("calling_code", data.calling_code);
      if (data.address) formData.append("address", data.address);
      if (data.country) formData.append("country", data.country);
      if (data.phone) formData.append("phone", data.phone);

      if (data.services && Array.isArray(data.services)) {
        data.services.forEach((service, index) => {
          formData.append(`services[${index}]`, service);
        });
      }

      if (data.work_type === "hourly" && data.hourly_rate) {
        formData.append("hourly_rate", data.hourly_rate);
      }
      if (data.work_type === "fixed") {
        if (data.fixed_price) formData.append("fixed_price", data.fixed_price);
        if (data.working_days) formData.append("working_days", data.working_days);
      }

      if (data.commission_per_booking) {
        formData.append("commission_per_booking", data.commission_per_booking);
      }
      if (data.employee_group) {
        formData.append("employee_group", data.employee_group);
      }

      if (data.imageFile) {
        formData.append("image", data.imageFile);
      }

      // Add emp_joining_date for both Create and Edit if present
      if (data.emp_joining_date) {
        formData.append("emp_joining_date", data.emp_joining_date);
      }

      // Add role_id, shift_id, and work_type for both Create and Edit
      if (data.role_id !== null && data.role_id !== undefined) {
        formData.append("role_id", data.role_id.toString());
      }
      if (data.shift_id !== null && data.shift_id !== undefined) {
        formData.append("shift_id", data.shift_id.toString());
      }
      if (data.work_type) {
        formData.append("work_type", data.work_type);
      }

      let response;

      if (editing && editing.id) {
        formData.append("_method", "PUT");
        response = await api.post(`/admin/employees/${editing.id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        response = await api.post("/admin/employees", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success || response.data) {
        setIsSidebarOpen(false);
        setEditing(null);
        await fetchEmployees();
        toast.success(`Employee ${editing ? "updated" : "created"} successfully!`);
      }
    } catch (err: any) {
      let msg = err.response?.data?.message || err.message || "Failed to save employee. Please try again.";

      // Handle validation errors specifically
      if (err.response?.data?.errors) {
        const validationErrors = Object.values(err.response.data.errors).flat();
        if (validationErrors.length > 0) {
          // Join multiple errors with a newline or just show the first few
          msg = validationErrors.join("\n");
        }
      }

      toast.error(msg);
      console.error("Error saving employee:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;

    setIsDeleting(true);
    try {
      await api.delete(`/admin/employees/${deletingEmployee.id}`);
      setEmployees(prev => prev.filter(emp => emp.id !== deletingEmployee.id));
      toast.success("Employee deleted successfully!");
      setDeleteConfirmOpen(false);
      setDeletingEmployee(null);
    } catch (err: any) {
      console.error("Error deleting employee:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to delete employee");
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter employees
  const filteredEmployees = useMemo(() => {
    return employees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.mobile && emp.mobile.includes(searchTerm)) ||
        emp.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.group && emp.group.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [employees, searchTerm]);

  // Paginated employees
  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredEmployees.slice(startIndex, endIndex);
  }, [filteredEmployees, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const formatMobile = (mobile: string | null, callingCode: string | null) => {
    if (!mobile) return "—";
    if (callingCode) return `${callingCode} ${mobile}`;
    return mobile;
  };

  return (
    <div className="p-4 lg:p-6">


      <div className="flex flex-col w-full h-full">
        {/* Header */}
        <PageHeader
          title="Employees"
          description="Manage your team members and staff"
          icon={Users}
          variant="card"
          className="mb-0"
          mobileActionsInHeader={true}
          actions={
            !isSidebarOpen && (
              <button
                onClick={handleCreateNew}
                className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
              >
                <Plus className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                <span>Add Employee</span>
              </button>
            )
          }
        />

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
          {/* Left: Per Page Selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
            <Select
              value={itemsPerPage.toString()}
              onValueChange={(value) => {
                setItemsPerPage(Number(value));
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[80px] bg-white border-gray-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
          </div>

          {/* Right: Search */}
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 pb-6 min-h-0 flex flex-col">
          <div className="flex-1 rounded-xl border-0 lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
            <div className="flex-1 lg:overflow-y-auto relative">
              <table className="w-full border-separate border-spacing-y-4 lg:border-collapse lg:border-spacing-0">
                <thead className="hidden lg:table-header-group sticky top-0 z-10 bg-[#885ABB]">
                  <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
                    <th className="col-span-3 text-left font-bold">Employee</th>
                    <th className="col-span-2 text-left font-bold">Contact</th>
                    <th className="col-span-2 text-left font-bold">Role</th>
                    <th className="col-span-2 text-left font-bold">Group</th>
                    <th className="col-span-1 text-center font-bold">Services</th>
                    <th className="col-span-2 text-right font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="flex flex-col gap-4 lg:table-row-group lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white">
                  {fetchLoading ? (
                    <tr className="flex lg:table-row h-64 w-full items-center justify-center">
                      <td colSpan={12} className="flex h-full w-full items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr className="flex lg:table-row h-64 w-full items-center justify-center">
                      <td colSpan={12} className="flex flex-col h-full w-full items-center justify-center text-center px-6">
                        <p className="text-lg font-semibold text-gray-900 mb-2">
                          Unable to load employees
                        </p>
                        <p className="text-gray-500 mb-6">{error}</p>
                        <button
                          onClick={fetchEmployees}
                          className="px-6 py-2 rounded-lg bg-[#885ABB] text-white font-medium hover:bg-[#6f47a2]"
                        >
                          Try Again
                        </button>
                      </td>
                    </tr>
                  ) : paginatedEmployees.length === 0 ? (
                    <tr className="flex flex-col h-64 text-gray-400 w-full lg:grid lg:grid-cols-12">
                      <td className="col-span-12 w-full h-full flex flex-col items-center justify-center">
                        <Search className="w-12 h-12 mb-3 opacity-20 mx-auto" />
                        <p className="text-lg font-medium text-gray-500">
                          No employees found
                        </p>
                        <p className="text-sm">Try adjusting your search</p>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {paginatedEmployees.map((emp) => (
                        <tr
                          key={emp.id}
                          className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-stretch lg:items-center px-5 py-5 lg:px-6 lg:py-4 bg-white lg:hover:bg-purple-50/30 transition-colors border lg:border-0 border-gray-200 rounded-xl lg:rounded-none shadow-sm lg:shadow-none relative group"
                          onClick={() => handleViewDetails(emp)}
                        >
                          <td className="col-span-3">
                            <div className="flex items-center gap-4">
                              <div className="h-12 w-12 lg:h-10 lg:w-10 rounded-full bg-purple-100 flex items-center justify-center font-bold text-purple-600 overflow-hidden flex-shrink-0 border-2 border-white lg:border-0 shadow-sm lg:shadow-none">
                                {emp.image && emp.image !== "https://salonify.co.uk/img/default-avatar-user.png" ? (
                                  <img
                                    src={emp.image}
                                    alt={emp.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  emp.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold lg:font-semibold text-gray-800 lg:group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                                  {emp.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{emp.email}</p>
                              </div>
                            </div>
                          </td>

                          <td className="col-span-2 lg:text-gray-700 truncate">
                            <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Contact</span>
                            <span className="text-sm font-medium lg:font-normal">{formatMobile(emp.mobile, emp.calling_code)}</span>
                          </td>

                          <td className="col-span-2">
                            <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Role</span>
                            <span
                              className={cn(
                                "inline-flex items-center justify-center px-3 py-1 lg:px-2.5 lg:py-0.5 rounded-full text-xs font-bold lg:font-semibold border",
                                emp.role === "Administrator"
                                  ? "bg-blue-100 text-blue-700 border-blue-200"
                                  : "bg-green-100 text-green-700 border-green-200"
                              )}
                            >
                              {emp.role}
                            </span>
                          </td>

                          <td className="col-span-2 lg:text-gray-700 truncate">
                            <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Group</span>
                            <span className="text-sm font-medium lg:font-normal">{emp.group || "—"}</span>
                          </td>

                          <td className="col-span-1 lg:text-center">
                            <span className="lg:hidden text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Services</span>
                            <span className="text-sm font-bold text-gray-900 bg-gray-100 lg:bg-transparent px-2 py-0.5 rounded lg:p-0">
                              {emp.services?.length || 0}
                            </span>
                          </td>

                          <td className="col-span-2 flex justify-end gap-2 pt-4 lg:pt-0 mt-4 lg:mt-0 border-t lg:border-0 border-gray-100">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(emp);
                              }}
                              className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                            >
                              <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                              <span className="lg:hidden">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingEmployee(emp);
                                setDeleteConfirmOpen(true);
                              }}
                              className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                            >
                              <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                              <span className="lg:hidden">Delete</span>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {!fetchLoading && !error && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 mt-4 border-t border-gray-200 bg-white rounded-b-xl">
              <div className="text-sm text-gray-600 text-center sm:text-left">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredEmployees.length)} of {filteredEmployees.length} employees
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 whitespace-nowrap">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <EmployeeSidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setError(null);
        }}
        initialData={editing || undefined}
        isSaving={loading}
        onSubmit={handleSubmit}
      />


      <EmployeeDetailsSidebar
        isOpen={isDetailsSidebarOpen}
        onClose={() => {
          setIsDetailsSidebarOpen(false);
          setSelectedEmployee(null);
        }}
        employee={selectedEmployee}
      />

      <DeleteConfirmModal
        isOpen={deleteConfirmOpen}
        type="employee"
        name={deletingEmployee?.name || ""}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeletingEmployee(null);
        }}
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
